'use client';

import React from 'react';
import styled, { useTheme } from 'styled-components';
import { getDeltaColorBySign, formatSigned } from '@/utils/metrics';

export interface DeltaProps {
  value: number;
  changeLabel?: string;
  changeReverse?: boolean;
  className?: string;
  'aria-label'?: string;
}

const DeltaSpan = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

/**
 * Delta Component
 * 
 * Displays a numeric delta with sign-based coloring:
 * - Positive values: green (theme.colors.success)
 * - Negative values: red (theme.colors.error)
 * - Zero values: neutral gray (theme.colors.text.muted)
 * 
 * @component
 * @example
 * ```tsx
 * <Delta value={5} />   // +5 in green
 * <Delta value={-3} />  // -3 in red
 * <Delta value={0} />   // 0 in gray
 * ```
 */
export const Delta: React.FC<DeltaProps> = ({ value, changeLabel, changeReverse = false, className, ...rest }) => {
  const theme = useTheme();
  const color =  getDeltaColorBySign(value, theme,changeReverse);
  
  // If changeReverse is true, always display with minus sign (take absolute value and add minus)
  let text: string;
  let arrow: string;
  
    text = formatSigned(value);
    arrow = changeReverse ? value > 0 ? '↓' : '↑' : value > 0 ? '↑' : value < 0 ? '↓' : '';
  
  
  return (
    <DeltaSpan $color={color} className={className} {...rest}>
      {text}{arrow}{changeLabel && <span style={{ color: theme.colors.gray[900] }}>{changeLabel}</span>}
    </DeltaSpan>
  );
};

export default Delta;

